import Close from '@components/icons/Close/Close'
import Union from '@components/icons/Union/Union'
import ClickOutside from '@lib/click-outside'
import clsx from 'clsx'
import gsap from 'gsap'
import Flip from 'gsap/dist/Flip'
import { useQueryState } from 'next-usequerystate'
import React, { PropsWithChildren, useCallback, useRef } from 'react'
import s from './MobileFilter.module.scss'

interface Props {
  tags: string[]
  rootRef: React.MutableRefObject<HTMLDivElement>
}
type NativeAttrs = Omit<React.HTMLAttributes<HTMLDivElement>, keyof Props>

export type MobileFilterProps = Props & NativeAttrs

const MobileFilter = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<MobileFilterProps>
>(({ className, children, tags, rootRef }, ref) => {
  const [tagFilter, setTagFilter] = useQueryState('category')
  gsap.registerPlugin(Flip)
  const filterListRef = useRef<HTMLDivElement>(null)
  const filtersRef = useRef<HTMLUListElement>(null)
  const closeRef = useRef<HTMLDivElement>(null)
  const tl = useRef<gsap.core.Timeline>()

  const handleFliterClick = () => {
    if (tl.current) return
    const wrapper = document.querySelector(`.${s.wrapper}`)
    tl.current = gsap
      .timeline()
      .to(wrapper, { width: '60%', duration: 0.2 })
      .add(() => {
        const state = Flip.getState([filterListRef.current, wrapper])
        gsap.set([filterListRef.current, closeRef.current], { display: 'flex' })
        gsap.set(filtersRef.current, { display: 'block' })
        Flip.from(state, {
          duration: 0.25,
        })
      })
  }

  const handleCloseClick = () => {
    const filters = Array.from(
      document.querySelectorAll(`[data-filter-type]`)
    ) as HTMLLIElement[]

    const filterNames = Array.from(
      document.querySelectorAll(`.${s.filterValueName}`)
    ) as HTMLDivElement[]

    const filterValueList = Array.from(
      document.querySelectorAll(`.${s.filterValuesList}`)
    ) as HTMLUListElement[]
    const wrapper = document.querySelector(`.${s.wrapper}`)

    const state = Flip.getState([
      filterListRef.current,
      wrapper,
      ...filters,
      ...filterValueList,
    ])

    gsap.set([filterListRef.current, closeRef.current, ...filterValueList], {
      display: 'none',
    })

    gsap.set([...filters, ...filterNames], { display: 'block' })

    Flip.from(state, {
      duration: 0.25,
      onComplete: () => {
        if (tl.current) {
          tl.current?.reverse()
          tl.current = undefined
        }
      },
    })
  }

  const handleFilterClick = useCallback(
    (filter: string) => {
      const rootBounding = rootRef.current.getBoundingClientRect()
      //make sure that the current scroll position is not above the root element
      if (window.scrollY > rootBounding.top) {
        window.scrollTo({ top: rootBounding.top, behavior: 'smooth' })
      }

      if (tagFilter == filter) {
        setTagFilter(null)
        return
      }

      setTagFilter(filter)
    },
    [tagFilter, setTagFilter]
  )
  return (
    <ClickOutside active={true} onClick={handleCloseClick}>
      <div className={s.stickyWrapper}>
        <div className={s.spacer}></div>
        <div data-fixed-scroll className={s.root}>
          <div className={s.wrapper}>
            <div className={s.filterBtn} onClick={handleFliterClick}>
              {/* <div className={s.backBtn} onClick={handleFilterBackClick}>
              {activeFilterSection && <SidebarArrow />}
            </div> */}
              <Union fill="#fff" /> Filter
            </div>
            <div className={s.filterList} ref={filterListRef}>
              {/* Filters */}
              <ul className={s.filters} ref={filtersRef}>
                {tags.map((filter, index) => (
                  <li
                    // data-filter-type={filter.type}
                    key={index}
                    onClick={(e) => handleFilterClick(filter)}
                  >
                    <div
                      className={clsx(s.filterValueName)}
                      data-active={tagFilter == filter}
                    >
                      <span>{filter}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className={s.close} ref={closeRef}>
              <Close color="#FFFFFF" size={'24px'} onClick={handleCloseClick} />
            </div>
          </div>
        </div>
      </div>
    </ClickOutside>
  )
})

MobileFilter.displayName = 'MobileFilter'
export default MobileFilter
